import * as BotConstants from './constants';
import { randomId } from './core.utils';
import { MessageType, MemberType } from '../enum';
import { BaseMessage, BaseSystemMessage, ConnectToAgent, MemberKind, RequiredMessage, SystemArticleMessage, SystemListMessage, TypingMessage, UserTransactionMessage, SystemArticleCheckFailureMessage, BaseEvent, MessageConfiguration, UserMessageForAI, AiMessageForUser } from '../types';
import { MediaMessage } from '../types/messages/MediaMessage';

export function createTypingMessage (id?: string|number|null, memberType: MemberKind = MemberType.AGENT): TypingMessage {
  return spawnSystemMessage<TypingMessage>({
    type: MessageType.TYPING,
    message: '',
    id: id || `loading-${randomId()}`,
    timestamp: Date.now(),
    ...(memberType && [MemberType.AGENT, MemberType.VISITOR].includes(memberType) ? { memberType } : null),
  });
}

type MessageWithoutType<T extends BaseMessage = BaseMessage, Optional extends boolean = true> = Optional extends true ? Partial<Omit<T, 'type'>> : Omit<T, 'type'>;

export function spawnMessage<T extends BaseMessage> (part: Partial<T> & Pick<BaseMessage, 'type' | 'memberType'>): T {
  return Object.assign({}, {
    message: '',
    nick: 'agent',
    displayName: 'Agent',
    timestamp: Date.now() + 1,
    id: `${part.type}-${randomId()}`,
  }, part) as T;
}

export function spawnSystemMessage<T extends BaseSystemMessage> (part: Partial<T> & Pick<BaseSystemMessage, 'type'>): T {
  return spawnMessage<T>({ history: [], memberType: MemberType.AGENT, nick: 'agent:system', displayName: 'Chat Agent', ...part });
}

export function spawnCategoryListMessage (message: MessageWithoutType<SystemListMessage>) {
  return spawnSystemMessage<SystemListMessage>({ type: MessageType.CATEGORIES_LIST, ...message });
}

export function spawnSectionListMessage (message: MessageWithoutType<SystemListMessage>) {
  return spawnSystemMessage<SystemListMessage>({ type: MessageType.SECTION_LIST, ...message });
}

export function spawnArticleListMessage (message: MessageWithoutType<SystemListMessage>) {
  return spawnSystemMessage<SystemListMessage>({ type: MessageType.ARTICLE_LIST, ...message });
}

export function spawnUserSystemMessage (message: Partial<BaseMessage> & RequiredMessage): BaseSystemMessage {
  return spawnMessage({ type: MessageType.USER_SYSTEM_MESSAGE, nick: 'sys:user', displayName: 'Chat Visitor', sendToChat: true, ...message, memberType: MemberType.VISITOR });
}

export function spawnUserSearchArticleMessage (message: Partial<BaseMessage> & RequiredMessage): BaseSystemMessage {
  return spawnUserSystemMessage({ type: MessageType.USER_SEARCH_ARTICLE_MESSAGE, nick: 'sys:user', displayName: 'Chat Visitor', sendToChat: true, ...message, memberType: MemberType.VISITOR });
}

export function spawnResolutionMessage (message: MessageWithoutType<BaseSystemMessage>) {
  return spawnSystemMessage({ type: MessageType.RESOLUTION, message: message.message || BotConstants.DEFAULT_RESOLUTION_MESSAGE, sendToChat: false, ...message });
}

export function spawnUserTransactionMessage (message: MessageWithoutType<UserTransactionMessage> & RequiredMessage & Pick<UserTransactionMessage, 'issues'>) {
  return spawnUserSystemMessage({ type: MessageType.USER_TRANSACTIONS, ...message });
}

export function spawnChatMessage (message: MessageWithoutType & RequiredMessage & Pick<BaseMessage, 'memberType'>) {
  return spawnMessage({ type: MessageType.CHAT_MESSAGE, ...message });
}

export function spawnMediaMessage (message: MessageWithoutType<MediaMessage> & Pick<BaseMessage, 'memberType'>) {
  return spawnMessage({ type: MessageType.MEDIA, ...message });
}

export function spawnAlternativeActionsMessage (message: Partial<BaseSystemMessage>) {
  return spawnMessage({ type: MessageType.ALTERNATIVE_ACTIONS, memberType: MemberType.AGENT, ...message });
}

export function spawnArticleCheckFailureMessage (message: MessageWithoutType<SystemArticleCheckFailureMessage> & RequiredMessage & Pick<SystemArticleCheckFailureMessage, 'messageSlots'>): SystemArticleCheckFailureMessage {
  return spawnSystemMessage({ type: MessageType.ARTICLE_CHECK_FAILED_MESSAGE, ...message });
}

export function spawnUserMessageForAI(message: MessageWithoutType<UserMessageForAI> & RequiredMessage) {
  return spawnUserSystemMessage({ type: MessageType.USER_MESSAGE_FOR_AI, sendToChat: true, ...message });
}

export function spawnSystemMessageFromAI(message: MessageWithoutType<AiMessageForUser> & RequiredMessage): AiMessageForUser {
  return spawnSystemMessage({ type: MessageType.AI_CHAT_MESSAGE, sendToChat: true, ...message });
}

/**
 * Create a message id for the Resolution message. This is used to generate a unique
 * resolution/action message for a given article.
 */
export function generateResolutionIdFromArticle (message: SystemArticleMessage) {
  return `${message.article.id}-resolution`;
}

/**
 * Create a message id for the Resolution message. This is used to generate a unique
 * resolution/action message for a given article.
 */
export function generateUserSideArticleMessageId (id: string | number) {
  return `user-side-article-${id}-${randomId()}`;
}

export function isMessageUserSideArticle (id?: string | number, articleId?: string | number) {
  return typeof id === 'string' && id?.startsWith(`user-side-article-${articleId}`);
}

/**
 * Return true if the connect to agent configuration is valid and the user can connect to agent.
 *
 * NOTE: The typescript equality is used to validate that the configuration is non-null
 * @param connectConfig The connect to agent configuration
 */
export function canConnectToAgent (connectConfig?: Partial<ConnectToAgent> | null): connectConfig is ConnectToAgent {
  return Boolean(connectConfig && connectConfig.connect && (connectConfig.liveChat || connectConfig.openRequest || connectConfig.voiceChat) && (connectConfig.abTestingPassed ?? true));
}

export function canCheckMetadataAutomation(connectConfig?: Partial<ConnectToAgent> | null) {
  return connectConfig?.enableMetadataRules && !!connectConfig.metadataRules?.length;
}